import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, TextField, Divider, IconButton } from "@mui/material";
import { Form, FormikErrors, FormikTouched } from "formik";
import { useDropzone } from "react-dropzone";
import { AppConstants } from "../../../../types/Constants";
import FileUploadv2 from "../../../../components/FileUploadv2";
import AppConfirmDialog from "../../../../components/AppConfirmDialog";
import { convertByteString, getImageURLOnServer } from "../../../../utils/utils";
import { BeaconDialogModes } from "./BeaconDialog";
import Resizer from 'react-image-file-resizer';
import { MakeBeacon } from "../../../../types/models/MakeBeacon";
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import UploadImageModal from "../../../../components/UploadImageModal";
import { Point } from "react-easy-crop";
import EditIcon from '@mui/icons-material/Edit';
import { UploadParameters } from "./BeaconEditor";

interface BeaconStyleFormProps {
  imageId?: string,
  handleFileChange: (file: File | undefined, index: number, width: number, height: number) => void,
  mode?: typeof BeaconDialogModes[number]
  values: MakeBeacon,
  errors: FormikErrors<MakeBeacon>,
  handleChange: (e: React.ChangeEvent<any>) => void,
  setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => Promise<void | FormikErrors<MakeBeacon>>
  touched: FormikTouched<MakeBeacon>,
  uploads: UploadParameters[],
  setUploads: React.Dispatch<React.SetStateAction<UploadParameters[]>>,
}

const BeaconStyleForm: React.FC<BeaconStyleFormProps> = ({ handleFileChange, mode, values, errors, handleChange, setFieldValue, touched, uploads, setUploads }) => {

  const [uploadedFileForScreen, setUploadedFileForScreen] = useState<File | undefined>(undefined);
  const [openFileSizeExceededMessage, setOpenFileSizeExceededMessage] = useState<boolean>(false);
  const [openFileDimensionsExceededMessage, setOpenFileDimensionsExceededMessage] = useState<boolean>(false);
  const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });
  const [tempImageURL, setTempImageURL] = useState<string>("");

  const [openImageUpload, setOpenImageUpload] = useState<boolean>(false);

  const oldFilterFileRef = useRef<File | undefined>();
  const oldImageFilterURLRef = useRef<string>("");

  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [editIndex, setEditIndex] = useState<number | null>(null);

  const maxFiles = 8;

  const dropzoneForScreen = useDropzone({
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.svg'],
    },
    maxFiles: maxFiles,
  });

  useEffect(() => {
    if (dropzoneForScreen.acceptedFiles) {
      // Check how many files the user can upload left in the available slots, and only upload remaining amount
      const allowedUploads = maxFiles - uploads.length;
      const filesToUpload = dropzoneForScreen.acceptedFiles.slice(0, allowedUploads);

      // No space left for uploads max 8
      if (filesToUpload.length === 0) {
        return;
      }

      const createNewUploads = async () => {

        const newUploadsProms = filesToUpload.map(async (file, index) => {
          let resizedFile = file;
          // Check image file size
          if (file.size > AppConstants.MaxImageBytes) {
            resizedFile = await new Promise<File>((resolve) => {
              resizeImage(file, AppConstants.MaxImageBytes, (resize: File) => {
                resolve(resize);
              });
            });
          }

          const imageURL = URL.createObjectURL(resizedFile);
          const originalImageURL = URL.createObjectURL(resizedFile);

          return new Promise<UploadParameters>((resolve) => {
            getImageDimensions(resizedFile, (width, height) => {
              resolve({
                imageId: file.name, 
                imageURL: imageURL,
                originalImageURL: originalImageURL,
                uploadedFile: resizedFile,
                originalFile: resizedFile,
                imageDimensions: { width: width, height: height },
                index: uploads.length + index,
              });
            });
          });
        });
        
        const newUploads = await Promise.all(newUploadsProms);
      
        // Update the uploads state
        setUploads((prevUploads) => {
          const updatedUploads = [...prevUploads, ...newUploads];
          
          if (filesToUpload.length === 1) {
            setUploadedFileForScreen(newUploads[0].originalFile);
            setTempImageURL(newUploads[0].originalImageURL);
            setEditIndex(newUploads[0].index);
            setOpenImageUpload(true);
          }

          return updatedUploads;

        });
      };
      createNewUploads();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropzoneForScreen.acceptedFiles]);
  
  useEffect(() => {

    const uploadedImages = values.image_ids;
    if (uploads.length === 0 && uploadedImages.length > 0) {
      const createUploads = uploadedImages.map((id, index) => ({
        imageId: id,
        imageURL: getImageURLOnServer(id),
        uploadedFile: undefined,
        originalImageURL: "",
        originalFile: undefined,
        imageDimensions: { width: 0, height: 0 },
        index: index

      }));

      setUploads(createUploads);
    }

  }, [values.image_ids, uploads.length, setUploads]);


  const getImageDimensions = (file: File, callback: (width: number, height: number) => void) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        callback(img.width, img.height);
      };
      img.src = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  };

  const resizeImage = (file: File, maxSize: number, callback: (resizedFile: File) => void) => {
    Resizer.imageFileResizer(
      file,
      1000, // new image max width
      1000, // new image max height
      'JPEG', // default type
      100, // new image quality
      0, // rotation degree
      (blobFile) => {
        if (blobFile instanceof Blob && blobFile.size <= maxSize) {
          const resizedFile = new File([blobFile], file.name);
          callback(resizedFile);
        } else {
          setOpenFileSizeExceededMessage(true);
        }
      },
      'blob' // output type
    );
  };
  
  const handleLink = (event: React.ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value;

    // Only prepend https:// if the link doesnt have http:// or https://
    if (value && !/^https?:\/\//i.test(value)) {
      value = 'https://' + value;
    } 

    setFieldValue("link", value);
  };
  
  const resetCropAndZoom = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const handleEditClick = (e: React.MouseEvent, file: File | undefined, url: string, index: number) => {
    if (file) {
      e.stopPropagation();
      setUploadedFileForScreen(file);
      setEditIndex(index);
      setTempImageURL(url);
      setOpenImageUpload(true);
    }
  };

  return (
    <>
      <Form style={{ width: "100%", marginTop: 16 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 500 }}>Image(s)</Typography>
        <Box 
        sx={{ 
          display: "inline-flex",
          maxWidth: "580px",
          gap: "16px", 
          marginTop: 2, 
          borderRadius: 2.5,
          p: 2,
          zIndex: 2,
          flexWrap: "wrap" 
        }}>
            {uploads.map((upload, index) => (
              <Box 
                key={index} 
                sx={{ 
                  position: "relative",
                  cursor: upload.uploadedFile ? "pointer" : "default"
                }}
                onClick={upload.uploadedFile ? (e) => handleEditClick(e, upload.originalFile, upload.originalImageURL, upload.index) : undefined}
              >
              <FileUploadv2
                key={index}
                readonly={mode === "READ_ONLY"}
                imageId={upload.imageId}
                imageURL={upload.imageURL}
                modal={true}
                onDelete={() => handleFileChange(upload.uploadedFile, index, 0, 0)}
              />
            {upload.uploadedFile && (
              <IconButton 
                size="small"
                onClick={(e) => handleEditClick(e, upload.originalFile, upload.originalImageURL, upload.index)} 
                sx={{ 
                  position: "absolute",
                  top: 4,
                  right: 4,
                  zIndex: 20,
                  borderRadius: "50%",
                  backgroundColor: "rgba(0,0,0,0.5)",
                  color: "white",
                  '&:hover': {
                    backgroundColor: "rgba(0,0,0,0.5)",
                  },
                }}
              >
                  <EditIcon sx={{ color: "white", fontSize: 18 }} />
              </IconButton>
            )}
            </Box>
          ))}
            {uploads.length < 8 && 
              <Box {...(mode !== "READ_ONLY" ? dropzoneForScreen.getRootProps({ className: 'dropzone' }) : {})}sx={{ 
                width: "128px",
                height: "128px",
                border: `dashed 2px ${dropzoneForScreen.isDragAccept ? 'purple' : 'silver'}`,
                cursor: mode !== "READ_ONLY" ? "pointer" : "default", 
                borderRadius: 2.5,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                color: "silver"
              }}>
              <input {...dropzoneForScreen.getInputProps()} />
                <ImageOutlinedIcon
                  style={{
                    fontSize: 40,
                  }}
                />
                <Typography>+ Add Image(s)</Typography>
              </Box>
            }
        </Box>
        <Divider sx={{ my: 4 }} />
        <Typography sx={{ fontSize: 16, fontWeight: 500 }}>Details Text</Typography>
        <TextField
          disabled={mode === "MOD_ONLY" || mode === "READ_ONLY"}
          sx={{ padding: 2, marginTop: 3 }}
          value={values.description}
          minRows={10}
          name="description"
          variant="outlined"
          multiline
          size="medium"
          fullWidth
          id="description"
          placeholder="..."
          InputLabelProps={{ shrink: true }}
          error={((errors.description ? true : undefined) && touched.description)}
          onChange={handleChange}
          helperText={errors.description && touched.description ? errors.description : `${values.description.length}/${1024}`}
          inputProps={{ maxLength: 1024 }}
          FormHelperTextProps={{
            sx: {
              textAlign: 'right',
            },
          }}
        />
        <Divider sx={{ my: 4 }} />
        <Typography sx={{ fontSize: 16, fontWeight: 500 }}>Link</Typography>
        <Typography sx={{ fontSize: 14, paddingLeft: 2, paddingTop: 2 }}>URL (Optional)</Typography>
        <TextField
          disabled={mode === "MOD_ONLY" || mode === "READ_ONLY"}
          sx={{ padding: 2 }}
          name="link"
          value={values.link}
          variant="outlined"
          fullWidth
          id="link"
          InputLabelProps={{ shrink: true }}
          onChange={handleChange}
          error={((errors.link ? true : undefined) && touched.link)}
          helperText={errors.link && touched.link ? errors.link : null}
        />
        <UploadImageModal 
          open={openImageUpload}
          onConfirm={() => setOpenImageUpload(false)}
          onDeny={() => {
            setOpenImageUpload(false);
            resetCropAndZoom();
          }}
          confirmButtonTitle={"Save"}
          denyButtonTitle={"Cancel"}
          setCurrentAssetFile={(croppedFile) => {
            if (editIndex !== null && croppedFile) {
              getImageDimensions(croppedFile, (width, height) => {
                const newImageURL = URL.createObjectURL(croppedFile);

                const updatedUpload: UploadParameters = {
                  ...uploads[editIndex],
                  uploadedFile: croppedFile,
                  imageURL: newImageURL,
                  imageDimensions: { width, height }
                };

                const updatedUploads = [...uploads];
                updatedUploads[editIndex] = updatedUpload;
                setUploads(updatedUploads);
              });
            }
            resetCropAndZoom();
          }}
          setCurrentImageURL={setTempImageURL}
          imageFile={uploadedFileForScreen}
          imageURL={tempImageURL}
          cropShape="rect"
          customAspectRatio={6/5}
          paddingValue={0.05}
          crop={crop}
          setCrop={setCrop}
          zoom={zoom}
          setZoom={setZoom}
        />
        <AppConfirmDialog
          open={openFileSizeExceededMessage}
          onConfirm={() => setOpenFileSizeExceededMessage(false)}
          confirmButtonTitle="OK"
          disableDenyButton={true}
          title={`The image is too large. Beacon images have a maximum filesize of ${convertByteString(AppConstants.MaxImageBytes)}. Please try resizing the image then uploading it again.`}
          dialogTitle="Problem attaching image to beacon"
          content=""
        />
        <AppConfirmDialog
          open={openFileDimensionsExceededMessage}
          onConfirm={() => setOpenFileDimensionsExceededMessage(false)}
          confirmButtonTitle="OK"
          disableDenyButton={true}
          title={`The image is not 256x256. Please try resizing the image then uploading it again.`}
          dialogTitle="Problem attaching image to beacon"
          content=""
        />
      </Form>
    </>
  );
};

export default BeaconStyleForm;
